/**
 * 梦灵动态：CharacterMoment 的小时级公共事实时间线。
 *
 * 这里同时展示当前有效事实和被高优先级事件覆盖的 fallback；后者是审计线索，
 * 不能因为用户最终没看到就从管理端消失。
 */

import { useMemo, useState } from "react";
import {
  Alert,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Pagination,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { api, type Paged } from "../../services/api";
import { useCharacter } from "../../services/character";
import { useTableParams } from "../../components/DataTable";
import { fmtDateTime } from "../../components/format";

type MomentSource =
  | "fallback"
  | "routine"
  | "conversation_inspiration"
  | "spirit_adventure"
  | "map_adventure"
  | "livestream"
  | "operator";

interface MomentBlocker {
  id: string;
  sourceType: MomentSource;
  kind: string;
  title: string | null;
  startsAt: string;
  endsAt: string | null;
  priority: number;
}

interface CharacterMoment {
  id: string;
  characterId: string;
  characterName: string;
  sourceType: MomentSource;
  kind: string;
  sourceRef: string | null;
  idempotencyKey: string;
  startsAt: string;
  endsAt: string | null;
  title: string | null;
  narrative: string;
  factSummary: string;
  activity: string | null;
  world: "spirit_realm" | "earth_projection";
  locationName: string | null;
  mood: string | null;
  tags: string[];
  importance: number;
  priority: number;
  provenance: unknown;
  deidentified: boolean;
  visibility: string;
  status: string;
  projectionState: "effective" | "suppressed" | "inactive";
  suppressedBy: MomentBlocker | null;
  createdAt: string;
  updatedAt: string;
}

const FILTER_KEYS = ["search", "from", "to", "sourceType", "kind", "world", "visibility", "status"];
const PAGE_SIZE = 50;

const SOURCE_META: Record<MomentSource, { label: string; color: string }> = {
  fallback: { label: "日常兜底", color: "default" },
  routine: { label: "固定作息", color: "blue" },
  conversation_inspiration: { label: "对话灵感", color: "purple" },
  spirit_adventure: { label: "灵境冒险", color: "cyan" },
  map_adventure: { label: "地图冒险", color: "green" },
  livestream: { label: "直播", color: "red" },
  operator: { label: "运营事件", color: "gold" },
};

export function CharacterMomentsPage() {
  const { characterId } = useCharacter();
  const { searchParams, params: filters, setParam, setParams } = useTableParams(FILTER_KEYS);
  const [selected, setSelected] = useState<CharacterMoment | null>(null);
  const page = Number(searchParams.get("page") ?? 1);
  const pageSize = Number(searchParams.get("pageSize") ?? PAGE_SIZE);
  const queryParams = {
    ...filters,
    ...(characterId ? { characterId } : {}),
    page,
    pageSize,
  };

  const list = useQuery({
    queryKey: ["characterMoments", queryParams],
    queryFn: () =>
      api.get<Paged<CharacterMoment>>("/api/v1/admin/diaries/moments", queryParams),
    placeholderData: keepPreviousData,
  });

  const groups = useMemo(() => {
    const result = new Map<string, CharacterMoment[]>();
    for (const moment of list.data?.items ?? []) {
      const date = dayjs(moment.startsAt).format("YYYY-MM-DD");
      result.set(date, [...(result.get(date) ?? []), moment]);
    }
    return [...result.entries()];
  }, [list.data?.items]);

  const from = searchParams.get("from");
  const to = searchParams.get("to");

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Tabs
        activeKey="moments"
        items={[
          { key: "moments", label: "已发生 Moment" },
          { key: "candidates", label: "待筛选灵感（预留）", disabled: true },
        ]}
      />

      <Alert
        type="info"
        showIcon
        message="这是所有用户共享的角色事实时间线"
        description="Moment 使用第三人称记录。被直播、冒险等事件覆盖的 fallback 不会进入有效时间线，但仍保留在这里供审计。"
      />

      <Card styles={{ body: { padding: 16 } }}>
        <Space wrap size={[10, 10]}>
          <Input.Search
            allowClear
            style={{ width: 280 }}
            placeholder="标题、叙事、事实、活动、地点"
            defaultValue={searchParams.get("search") ?? ""}
            onSearch={(value) => setParam("search", value)}
          />
          <DatePicker.RangePicker
            value={from && to ? [dayjs(from), dayjs(to)] : null}
            onChange={(dates) =>
              setParams({
                from: dates?.[0]?.format("YYYY-MM-DD"),
                to: dates?.[1]?.format("YYYY-MM-DD"),
              })
            }
          />
          <Select
            style={{ width: 140 }}
            value={searchParams.get("sourceType") ?? "all"}
            onChange={(value) => setParam("sourceType", value === "all" ? undefined : value)}
            options={[
              { value: "all", label: "全部来源" },
              ...Object.entries(SOURCE_META).map(([value, meta]) => ({ value, label: meta.label })),
            ]}
          />
          <Input.Search
            allowClear
            style={{ width: 190 }}
            placeholder="事件类型 kind"
            defaultValue={searchParams.get("kind") ?? ""}
            onSearch={(value) => setParam("kind", value)}
          />
          <Select
            style={{ width: 140 }}
            value={searchParams.get("world") ?? "all"}
            onChange={(value) => setParam("world", value === "all" ? undefined : value)}
            options={[
              { value: "all", label: "全部世界" },
              { value: "spirit_realm", label: "灵境" },
              { value: "earth_projection", label: "现实投影" },
            ]}
          />
          <Select
            style={{ width: 120 }}
            value={searchParams.get("status") ?? "active"}
            onChange={(value) => setParam("status", value)}
            options={[
              { value: "active", label: "有效记录" },
              { value: "hidden", label: "已隐藏" },
              { value: "purged", label: "已清除" },
            ]}
          />
          <Typography.Text type="secondary">共 {list.data?.total ?? 0} 条</Typography.Text>
        </Space>
      </Card>

      {list.error && <Alert type="error" showIcon message={(list.error as Error).message} />}

      <Spin spinning={list.isFetching}>
        {groups.length === 0 && !list.isFetching ? (
          <Card><Empty description="没有匹配的梦灵动态" /></Card>
        ) : (
          groups.map(([date, moments]) => (
            <Card key={date} title={date} style={{ marginBottom: 16 }}>
              <Timeline
                mode="left"
                items={moments.map((moment) => ({
                  color: timelineColor(moment),
                  label: timeRange(moment),
                  children: <MomentCard moment={moment} onClick={() => setSelected(moment)} />,
                }))}
              />
            </Card>
          ))
        )}
      </Spin>

      {(list.data?.total ?? 0) > pageSize && (
        <Pagination
          current={page}
          pageSize={pageSize}
          total={list.data?.total ?? 0}
          showSizeChanger={false}
          onChange={(next) => setParam("page", next)}
          style={{ textAlign: "center" }}
        />
      )}

      <MomentDrawer moment={selected} onClose={() => setSelected(null)} />
    </Space>
  );
}

function MomentCard({ moment, onClick }: { moment: CharacterMoment; onClick: () => void }) {
  const source = SOURCE_META[moment.sourceType];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onClick();
      }}
      style={{ cursor: "pointer", padding: "2px 0 14px" }}
    >
      <Space size={[6, 6]} wrap style={{ marginBottom: 8 }}>
        <Tag color={source.color}>{source.label}</Tag>
        <Tag>{moment.kind}</Tag>
        <Tag color={moment.world === "earth_projection" ? "green" : "blue"}>
          {moment.world === "earth_projection" ? "现实投影" : "灵境"}
        </Tag>
        <ProjectionTag state={moment.projectionState} />
        {moment.locationName && <Tag>{moment.locationName}</Tag>}
      </Space>
      <Typography.Title level={5} style={{ margin: "0 0 6px" }}>
        {moment.title || moment.activity || "（无标题）"}
      </Typography.Title>
      <Typography.Paragraph style={{ marginBottom: 6, lineHeight: 1.8 }}>
        {moment.narrative}
      </Typography.Paragraph>
      <Typography.Text type="secondary">事实：{moment.factSummary}</Typography.Text>
      {moment.suppressedBy && (
        <Alert
          type="warning"
          showIcon
          message={`被 ${SOURCE_META[moment.suppressedBy.sourceType].label} / ${moment.suppressedBy.kind} 覆盖`}
          style={{ marginTop: 10 }}
        />
      )}
    </div>
  );
}

function MomentDrawer({ moment, onClose }: { moment: CharacterMoment | null; onClose: () => void }) {
  return (
    <Drawer title="Moment 详情" width={680} open={Boolean(moment)} onClose={onClose}>
      {moment && (
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="角色">{moment.characterName}</Descriptions.Item>
            <Descriptions.Item label="投影"><ProjectionTag state={moment.projectionState} /></Descriptions.Item>
            <Descriptions.Item label="来源">{SOURCE_META[moment.sourceType].label}</Descriptions.Item>
            <Descriptions.Item label="事件类型">{moment.kind}</Descriptions.Item>
            <Descriptions.Item label="开始">{fmtDateTime(moment.startsAt)}</Descriptions.Item>
            <Descriptions.Item label="结束">{fmtDateTime(moment.endsAt)}</Descriptions.Item>
            <Descriptions.Item label="世界">{moment.world === "earth_projection" ? "现实投影" : "灵境"}</Descriptions.Item>
            <Descriptions.Item label="地点">{moment.locationName || "—"}</Descriptions.Item>
            <Descriptions.Item label="优先级">{moment.priority}</Descriptions.Item>
            <Descriptions.Item label="重要度">{moment.importance}</Descriptions.Item>
            <Descriptions.Item label="可见性">{moment.visibility}</Descriptions.Item>
            <Descriptions.Item label="状态">{moment.status}</Descriptions.Item>
            <Descriptions.Item label="去标识化">{moment.deidentified ? "已通过" : "未通过"}</Descriptions.Item>
            <Descriptions.Item label="来源引用">{moment.sourceRef || "—"}</Descriptions.Item>
            <Descriptions.Item label="幂等键" span={2}><span className="mono">{moment.idempotencyKey}</span></Descriptions.Item>
          </Descriptions>

          <DetailText label="第三人称叙事" value={moment.narrative} />
          <DetailText label="事实摘要" value={moment.factSummary} />
          <DetailText label="结构化活动" value={moment.activity} />
          <div>
            <Typography.Text type="secondary">标签</Typography.Text>
            <div style={{ marginTop: 8 }}><Space wrap>{moment.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}</Space></div>
          </div>
          {moment.suppressedBy && (
            <Alert
              type="warning"
              showIcon
              message="这条 fallback 没有进入有效时间线"
              description={`覆盖事件：${SOURCE_META[moment.suppressedBy.sourceType].label} / ${moment.suppressedBy.kind}（优先级 ${moment.suppressedBy.priority}）`}
            />
          )}
          {moment.provenance != null && (
            <div>
              <Typography.Text type="secondary">溯源信息</Typography.Text>
              <pre className="mono" style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
                {JSON.stringify(moment.provenance, null, 2)}
              </pre>
            </div>
          )}
        </Space>
      )}
    </Drawer>
  );
}

function DetailText({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <div style={{ marginTop: 8, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{value}</div>
    </div>
  );
}

function ProjectionTag({ state }: { state: CharacterMoment["projectionState"] }) {
  if (state === "effective") return <Tag color="success">有效事实</Tag>;
  if (state === "suppressed") return <Tag color="warning">已覆盖</Tag>;
  return <Tag>未进入投影</Tag>;
}

function timelineColor(moment: CharacterMoment): string {
  if (moment.projectionState === "suppressed") return "gray";
  if (moment.sourceType === "livestream") return "red";
  if (moment.sourceType === "operator") return "gold";
  if (moment.sourceType === "map_adventure") return "green";
  return "blue";
}

function timeRange(moment: CharacterMoment): string {
  const start = dayjs(moment.startsAt).format("HH:mm");
  if (!moment.endsAt) return `${start}–进行中`;
  return `${start}–${dayjs(moment.endsAt).format("HH:mm")}`;
}
